<template>
  <div ref="el" class="nav-item-dropdown h-full">
    <ClientOnly
      v-if="isExternalLink(menu) || menu.name?.toLowerCase() === 'source'"
    >
      <a
        :href="getExternalHref(menu)"
        :target="menu.target ? menu.target : '_self'"
        class="group relative flex cursor-pointer select-none items-center gap-2 text-sm xl:text-base py-4 xl:py-[22px] px-2 border-b-4 border-transparent h-full"
        :class="{
          '!border-p-500': isSelectedGroup,
          'dropdown-trigger': menu.groups && menu.groups.length,
        }"
        tabindex="0"
        @click="handleClick(menu.modal)"
      >
        <span
          class="text-s-900 font-bold transition-all duration-300 group-hover:text-opacity-60 leading-none"
          data-testid="navigation-menu"
        >
          {{ menu.name }}
        </span>
        <CommonTriangleDown
          v-if="menu.groups && menu.groups.length"
          class="transition duration-300 group-focus:rotate-180"
        />
      </a>
      <template #fallback>
        <div
          class="group relative flex cursor-pointer select-none items-center gap-2 text-sm xl:text-base py-4 xl:py-[22px] px-2 border-b-4 border-transparent h-full"
          :class="{
            '!border-p-500': isSelectedGroup,
            'dropdown-trigger': menu.groups && menu.groups.length,
          }"
        >
          <span
            class="text-s-900 font-bold transition-all duration-300 group-hover:text-opacity-60 leading-none"
            data-testid="navigation-menu"
          >
            {{ menu.name }}
          </span>
          <CommonTriangleDown
            v-if="menu.groups && menu.groups.length"
            class="transition duration-300 group-focus:rotate-180"
          />
        </div>
      </template>
    </ClientOnly>
    <component
      :is="setLink(menu)"
      v-else
      external
      class="group relative flex cursor-pointer select-none items-center gap-2 text-sm xl:text-base py-4 xl:py-[22px] px-2 border-b-4 border-transparent h-full"
      v-bind="setAdditionalAttributes(menu)"
      :class="{
        '!border-p-500': isSelectedGroup,
        'dropdown-trigger': menu.groups && menu.groups.length,
      }"
      tabindex="0"
      @click="handleClick(menu.modal)"
    >
      <span
        class="text-s-900 font-bold transition-all duration-300 group-hover:text-opacity-60 leading-none"
        data-testid="navigation-menu"
      >
        {{ menu.name }}
      </span>
      <CommonTriangleDown
        v-if="menu.groups && menu.groups.length"
        class="transition duration-300 group-focus:rotate-180"
      />
    </component>

    <!-- Submenu -->
    <div
      v-if="menu.groups && menu.groups.length"
      class="dropdown-menu top-10 z-50 -translate-x-1/2 pt-6"
      :class="[isProductsAndServices ? 'left-[55%] xl:left-1/2' : 'left-1/2']"
    >
      <!-- Submenu Arrow -->
      <div
        class="absolute left-1/2 top-4 z-[100] h-4 w-4 -translate-x-1/2 rotate-45 bg-white shadow-[-3px_-5px_10px_-1px_rgba(0,0,0,0.1)]"
      />
      <div class="sm-height:max-h-[60vh]">
        <div
          data-testid="navigation-item-container"
          class="text-s-900 flex max-h-[550px] flex-col flex-wrap gap-x-6 gap-y-6 rounded-xl bg-white p-6 shadow-[3px_3px_10px_rgba(0,0,0,0.25)] xl:max-h-[560px] xl:p-8"
          :class="{
            'xl:min-w-content-container min-w-[980px]': isProductsAndServices,
          }"
        >
          <div
            v-for="group in menu.groups"
            :key="`${menu.name}_${group.name}`"
            class="min-w-[230px]"
            :class="group.extraClass"
          >
            <div class="block">
              <div class="w-full">
                <h4
                  class="border-p-500 font-montserrat text-s-900 mb-3 border-b-[3px] pb-3 text-sm font-semibold uppercase xl:text-base xl:leading-5"
                >
                  {{ group.name }}
                </h4>

                <div
                  class="flex flex-col gap-y-1.5"
                  :class="{ 'flex !flex-row gap-x-3': group.hasSubMenu }"
                >
                  <div
                    v-for="child in group.children"
                    :key="`${group.name}_${child.name}`"
                  >
                    <!-- Sub Menu -->
                    <ItemSubMenu
                      v-if="child.children?.length"
                      :title="child.name"
                      :sub-menu="child.children"
                      @close="closeMenu"
                    />
                    <!-- Modal -->
                    <div
                      v-else-if="child.modal && checkIfCallback(child)"
                      class="mb-2 cursor-pointer font-normal hover:underline"
                      @click="handleClick(child.modal, child['initial-fields'])"
                    >
                      {{ child.name }}
                    </div>

                    <!-- Link -->
                    <div
                      v-else-if="!child.modal && checkIfCallback(child)"
                      class="flex gap-x-1.5"
                    >
                      <Icon
                        v-if="child.icon === 'source'"
                        name="sidebar/Terminal"
                        class="h-3.5 w-4"
                      />
                      <ClientOnly
                        v-if="
                          isExternalLink(child) ||
                          child.name?.toLowerCase() === 'source'
                        "
                      >
                        <a
                          :href="getExternalHref(child, group.href)"
                          :target="child.target ? child.target : '_self'"
                          data-testid="navigation-item"
                          :class="{
                            'bg-s-200': isSelected(child.href, group.href),
                          }"
                          class="font-montserrat hover:bg-s-200 flex items-center justify-between rounded-lg text-sm font-normal xl:text-base xl:leading-5"
                          @click="closeMenu"
                        >
                          <span class="max-w-[200px]">
                            {{ child.name }}
                          </span>
                          <span
                            v-if="hasAnyActiveMembership(child.memberships)"
                            class="h-4 w-4"
                          >
                            <Icon
                              name="check-subscribe"
                              class="text-p-500 h-full w-full"
                            />
                          </span>
                        </a>
                        <template #fallback>
                          <div
                            :class="{
                              'bg-s-200': isSelected(child.href, group.href),
                            }"
                            class="font-montserrat hover:bg-s-200 flex items-center justify-between rounded-lg text-sm font-normal xl:text-base xl:leading-5"
                          >
                            <span class="max-w-[200px]">
                              {{ child.name }}
                            </span>
                            <span
                              v-if="hasAnyActiveMembership(child.memberships)"
                              class="h-4 w-4"
                            >
                              <Icon
                                name="check-subscribe"
                                class="text-p-500 h-full w-full"
                              />
                            </span>
                          </div>
                        </template>
                      </ClientOnly>
                      <component
                        :is="setLink(child)"
                        v-else
                        v-bind="setAdditionalAttributes(child, group.href)"
                        data-testid="navigation-item"
                        :class="{
                          'bg-s-200': isSelected(child.href, group.href),
                        }"
                        external
                        class="font-montserrat hover:bg-s-200 flex items-center justify-between rounded-lg text-sm font-normal xl:text-base xl:leading-5"
                        @click="closeMenu"
                      >
                        <span class="max-w-[200px]">
                          {{ child.name }}
                        </span>
                        <span
                          v-if="hasAnyActiveMembership(child.memberships)"
                          class="h-4 w-4"
                        >
                          <Icon
                            name="check-subscribe"
                            class="text-p-500 h-full w-full"
                          />
                        </span>
                      </component>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import Icon from '~/components/common/Icon.vue'
import CommonTriangleDown from '~/components/common/TriangleDown.vue'
import ItemSubMenu from '~/components/navigation/ItemSubMenu.vue'
import { useMembershipsStore } from '~/stores/memberships'

const props = defineProps({
  data: {
    type: Object,
    required: true,
  },
  isSelectedGroup: {
    type: Boolean,
    default: false,
  },
  onClick: {
    type: Function,
    default: null,
  },
})

const route = useRoute()
const { $modal, $config } = useNuxtApp()
const membershipsStore = useMembershipsStore()

const menu = ref({ ...props.data, open: false })
const isProductsAndServices = computed(
  () => menu.value.name === 'Products & Services',
)
const el = ref(null)

onMounted(() => {
  if (import.meta.client) {
    window.addEventListener('click', closePopup)
  }
})

onBeforeUnmount(() => {
  if (import.meta.client) {
    window.removeEventListener('click', closePopup)
  }
})

function isSelected(menuPath, base = '') {
  const { fullPath } = route
  const childPath = menuPath.replace('/', '')

  if (childPath === '') return fullPath === base

  return (
    fullPath.includes(base) &&
    fullPath?.split('/')?.some((_item) => _item === childPath)
  )
}

function checkIfCallback(child) {
  if (!child.if_callback) {
    return true
  }

  return child.if_callback()
}

function isExternalLink(element) {
  if (!element?.href) return false

  // Ensure consistent rendering between server and client
  // by checking for common external link patterns
  return (
    element.href.startsWith('https') ||
    element.href.startsWith('http') ||
    element.href.includes('://') ||
    element.href.startsWith('//')
  )
}

function getExternalHref(element, _base = '') {
  let href = element.href

  if (element.name?.toLowerCase() === 'source') {
    href = $config.public.sourceUrl
  }

  return href
}

function setLink(element) {
  if (isExternalLink(element)) {
    return 'a'
  }

  if (hasLink(element)) {
    return hasTarget(element) && typeof element.target === 'string'
      ? 'a'
      : resolveComponent('NuxtLink')
  }

  return 'div'
}

function hasLink(element) {
  return !!element.href
}

function hasTarget(element) {
  return 'target' in element
}

function setAdditionalAttributes(element, base = '') {
  if (!hasLink(element)) return {}

  let href = element.href
  let isExternal = false

  if (element.name?.toLowerCase() === 'source') {
    href = $config.public.sourceUrl
    isExternal = true
  }

  const OVERVIEW = 'Overview'
  let url = `${base}${href}`
  if (element.firstItem && element.firstItem !== OVERVIEW) {
    url += `/${element.firstItem}`
  }

  return isExternal || isExternalLink(element) || href.startsWith('https')
    ? {
        href: href,
        target: element.target ? element.target : '_self',
      }
    : {
        to: url,
        'no-prefetch': true, // Add no-prefetch only to NuxtLink components
      }
}

function handleClick(modal, initialFields) {
  if (!modal) return toggle()

  if (initialFields) {
    $modal.setInitialFields(initialFields)
  }

  $modal.open(modal)
}

function toggle() {
  menu.value.open = !menu.value.open
}

function closeMenu() {
  menu.value.open = false
}

/**
 * Close the menu if the user clicks outside the menu
 */
function closePopup(e) {
  const target = e.target
  const element = el.value

  if (element && !element.contains(target)) {
    menu.value.open = false
  }
}

function hasAnyActiveMembership(memberships) {
  if (memberships) {
    return memberships.find((membership) =>
      membershipsStore.getSubscription(membership),
    )
  }
}
</script>

<style lang="scss" scoped>
.nav-item-dropdown {
  position: relative;

  .dropdown-menu:active {
    display: block;
    opacity: 1;
  }

  &:has(.dropdown-trigger:focus)::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 100%;
    cursor: pointer;
  }
}

.dropdown-trigger {
  position: relative;

  &:focus + .dropdown-menu {
    visibility: visible;
    opacity: 1;
  }
}

.dropdown-menu {
  position: absolute;
  top: 2.5rem;
  opacity: 0;
  visibility: hidden;
  transition:
    visibility 0.3s,
    opacity 0.3s ease;
}
</style>
